/**
 * What a browse page last showed, by page identity, for Back.
 *
 * Browse and home pages fetch their list in the browser (no server list since
 * 2026-10-12). Back from a listing remounted the page, which fetched page 1
 * again: until it landed the grid held whatever the product page had left in
 * the shared list (three tiles on prod), and a reader who had scrolled down
 * stared at an empty screen for seconds. The list they left is kept here
 * instead and put back at once.
 *
 * Grids keep a list (rememberBrowseList). Parts of a page that are not a list
 * keep their state (rememberPageState): home's tag rows past the category
 * groups, the near-you row and the budget row, so Back paints the same page at
 * the same height and the next load carries on from there.
 *
 * In memory, so it lives for the tab's client-side session: Back within the
 * app. A reload starts empty and fetches as before. Bounded, oldest out.
 */

const MAX_ENTRIES = 40;

const entries = new Map<string, unknown>();

function keep(key: string, value: unknown): void {
  // Re-inserted so the most recently shown page is the last to go.
  entries.delete(key);
  entries.set(key, value);
  // Map keys iterate in insertion order; the loop runs only while there are more than MAX_ENTRIES.
  while (entries.size > MAX_ENTRIES) {
    const [oldest] = entries.keys();
    entries.delete(oldest);
  }
}

/** Keep `list` as what the page `key` shows. An empty list is not kept. */
export function rememberBrowseList<T>(key: string, list: readonly T[]): void {
  if (!key || list.length === 0) return;
  keep(key, list);
}

/** The list page `key` last showed, or null. */
export function recallBrowseList<T>(key: string): readonly T[] | null {
  const value = entries.get(key);
  return Array.isArray(value) ? (value as readonly T[]) : null;
}

/** Keep `state` as what the part of a page named `key` shows. */
export function rememberPageState<T extends object>(key: string, state: T): void {
  if (!key) return;
  keep(`state:${key}`, state);
}

/** The state `key` last kept, or null. */
export function recallPageState<T extends object>(key: string): T | null {
  return (entries.get(`state:${key}`) as T | undefined) ?? null;
}

/** The page the browser is on, for keys: path and query. Empty on the server. */
export function currentPageKey(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
}

/** Test seam: forget everything. */
export function clearBrowseListCache(): void {
  entries.clear();
}
